import { cx } from "../../../utils/classNames";
import {
  getFieldId,
  getItemWeightLabel,
  getScaleLabel,
} from "../performanceUtils";
import RatingControl from "./RatingControl";
import ui from "../../../components/ui/ui.module.css";
import styles from "../Performance.module.css";

// `onChange` absent = read-only. `selfAnswer` shows the employee's self-rating chips.
const RatingSection = ({ section, answer = {}, onChange, selfAnswer }) => {
  const readOnly = !onChange;
  const scale = section.scale;

  const updateItem = (itemKey, changes) =>
    onChange({
      ...answer,
      items: {
        ...answer.items,
        [itemKey]: { ...answer.items?.[itemKey], ...changes },
      },
    });

  return (
    <>
      {scale?.labels?.length > 0 && (
        <div className={styles.scaleLegend}>
          {[...scale.labels]
            .sort((a, b) => b.value - a.value)
            .map((l) => (
              <span key={l.value}>
                <strong>{l.value}</strong> {l.label}
              </span>
            ))}
        </div>
      )}

      {(section.items ?? []).map((item) => {
        const itemAnswer = answer.items?.[item.key] ?? {};
        const selfRating = selfAnswer?.items?.[item.key]?.rating;

        return (
          <div
            key={item.key}
            id={getFieldId(section.key, item.key)}
            className={styles.item}
          >
            <div className={styles.itemHeader}>
              <span className={styles.itemTitle}>{item.title}</span>
              <span className={styles.sectionMeta}>
                Weight {getItemWeightLabel(section, item)}
              </span>
            </div>
            {item.description && (
              <p className={styles.itemDescription}>{item.description}</p>
            )}
            {item.kpis?.length > 0 && (
              <div className={styles.kpis}>
                {item.kpis.map((kpi) => (
                  <span key={kpi} className={styles.kpi}>
                    {kpi}
                  </span>
                ))}
              </div>
            )}

            <div className={styles.itemControls}>
              {readOnly ? (
                <span
                  className={cx(
                    styles.ratingValue,
                    itemAnswer.rating == null && styles.ratingMissing,
                  )}
                >
                  {getScaleLabel(scale, itemAnswer.rating)}
                </span>
              ) : (
                <RatingControl
                  scale={scale}
                  value={itemAnswer.rating ?? null}
                  label={item.title}
                  onChange={(rating) => updateItem(item.key, { rating })}
                />
              )}
              {typeof selfRating === "number" && (
                <span
                  className={styles.selfChip}
                  title={getScaleLabel(scale, selfRating)}
                >
                  Self: {selfRating}
                </span>
              )}
            </div>

            {section.allowItemComments &&
              (readOnly ? (
                itemAnswer.comment && (
                  <p className={styles.readOnlyText}>
                    <strong>Critical incidents:</strong> {itemAnswer.comment}
                  </p>
                )
              ) : (
                <>
                  <label
                    className={cx(ui.label, styles.commentLabel)}
                    htmlFor={`${getFieldId(section.key, item.key)}-comment`}
                  >
                    Critical incidents
                  </label>
                  <textarea
                    id={`${getFieldId(section.key, item.key)}-comment`}
                    rows={2}
                    maxLength={2000}
                    className={cx(ui.input, ui.textarea)}
                    value={itemAnswer.comment ?? ""}
                    onChange={(e) =>
                      updateItem(item.key, { comment: e.target.value })
                    }
                  />
                </>
              ))}
          </div>
        );
      })}

      {section.allowSectionComment &&
        (readOnly ? (
          answer.comment && (
            <p className={styles.readOnlyText}>
              <strong>Critical incidents:</strong> {answer.comment}
            </p>
          )
        ) : (
          <>
            <label
              className={cx(ui.label, styles.commentLabel)}
              htmlFor={`${getFieldId(section.key)}-comment`}
            >
              Critical incidents
            </label>
            <textarea
              id={`${getFieldId(section.key)}-comment`}
              rows={3}
              maxLength={2000}
              className={cx(ui.input, ui.textarea)}
              value={answer.comment ?? ""}
              onChange={(e) => onChange({ ...answer, comment: e.target.value })}
            />
          </>
        ))}
    </>
  );
};

export default RatingSection;
